import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { accent } from '../helpers';
import { useSalleResultat, useSyntheseAudio } from '../../../../hooks/useSalle';

/**
 * Template "salle" — ce que la salle a répondu, rendu à la salle.
 *
 * Voir docs/conferences/seminaire-QUESTIONS-SALLE.md. C'est l'unique slide du
 * deck dont le CONTENU vient du réseau : tout le reste tient dans le bundle.
 *
 * TROIS ZONES, ET L'ORDRE COMPTE :
 *  1. la FRÉQUENCE, à gauche — la première chose qu'ils ont donnée à la slide 3,
 *     et la seule qui les situe les uns par rapport aux autres ;
 *  2. le NUAGE DE MOTS, à droite — « l'IA, pour vous, en un mot » ;
 *  3. la SYNTHÈSE de leurs questions, en bas, sur toute la largeur, parce que
 *     c'est elle qui ouvre les questions-réponses.
 *
 * ⚠️ ELLE NE DOIT JAMAIS TOMBER EN PANNE DEVANT LA SALLE. Un backend endormi,
 * un wifi qui lâche, une synthèse pas encore déposée : chaque cas a son écran,
 * et aucun n'est une erreur technique. Sans réponse, elle affiche l'invitation
 * à poser les questions à voix haute — le repli prévu au conducteur, où Adrien
 * lit les questions depuis le prompteur.
 *
 * ⚠️ AUCUN FILTRE SUR LE NUAGE (décidé le 19/09, public professionnel) : tous
 * les mots reçus s'affichent, y compris ceux vus une seule fois. Ce qui est
 * écarté l'est côté synthèse, par le prompt, et jamais ici.
 *
 * ⚠️ Rien de nominatif ne peut arriver ici : `/api/salle/resultat` ne renvoie
 * ni les questions ni leur auteur — des nombres, des mots isolés, et un texte
 * de synthèse relu par Adrien avant d'être projeté.
 *
 * Champs de slide :
 *  - kicker  : petit label (ex. "Vos réponses")
 *  - title   : le titre (défaut : "La salle")
 *  - accent  : "cyan" (défaut) | "red" | "amber" | "violet"
 *
 * À VENIR : le TTS. La synthèse est aujourd'hui un texte lu par Adrien. Quand
 * la voix IA la lira (5e apparition), le MP3 arrivera par le même appel — il
 * n'y a qu'un lecteur à brancher sous l'encart, la place est gardée.
 */

const FREQUENCES = [
    { cle: 'plus3', libelle: '3 fois par jour ou plus' },
    { cle: 'jour', libelle: '1 à 2 fois par jour' },
    { cle: 'semaine', libelle: 'Quelques fois par semaine' },
    { cle: 'mois', libelle: 'Quelques fois par mois' },
    { cle: 'jamais', libelle: 'Jamais' },
];

/**
 * LE PLACEMENT — une vraie spirale, pas une grille.
 *
 * ⚠️ POURQUOI PAS UN `flex-wrap`. C'était la première version : les mots
 * rangés du plus gros au plus petit, déposés alternativement à gauche et à
 * droite pour que le dominant finisse au milieu. Ça se lisait comme ce que
 * c'était — une liste en lignes régulières, avec des gouttières alignées d'un
 * bout à l'autre. Un nuage de mots n'a pas de lignes : les mots s'emboîtent,
 * les petits comblent les creux laissés par les gros, et la masse a une forme.
 *
 * L'ALGORITHME est celui de tous les nuages de mots (d3-cloud et les autres),
 * en trente lignes et sans dépendance : on place les mots DU PLUS GROS AU PLUS
 * PETIT ; chacun part du centre et suit une spirale d'Archimède jusqu'à
 * trouver une place où son rectangle ne touche aucun de ceux déjà posés. Le
 * plus fréquent se retrouve donc au milieu — sans qu'on ait à l'y forcer, il
 * arrive quand tout est libre — et les suivants s'enroulent autour.
 *
 * ⚠️ MESURE AU CANVAS, et c'est ce qui rend le placement exact : `measureText`
 * donne la largeur réelle du mot dans sa police et sa taille, avant tout
 * rendu. Sans elle, il faudrait peindre, mesurer, puis repeindre — la salle
 * verrait le nuage se réarranger sous ses yeux.
 *
 * ⚠️ LA SPIRALE EST APLATIE (`RATIO_SPIRALE`) : une spirale ronde donne un
 * nuage rond, alors que la zone est deux fois plus large que haute. On étire
 * donc le pas horizontal, et la masse épouse l'écran.
 */
/* Calé sur la forme de la zone (environ 1120 × 645, soit 0,58) : plus plat,
   le nuage laisse deux bandes vides en haut et en bas ; plus rond, il déborde
   en hauteur avant d'avoir rempli la largeur. */
const RATIO_SPIRALE = 0.56;   // hauteur / largeur du pas de spirale
const MARGE = 0.14;           // espace autour de chaque mot, en part de sa taille

/** Un canvas hors écran, créé une fois : il ne sert qu'à mesurer du texte. */
let _mesureur = null;
function mesurer(texte, police) {
    if (!_mesureur) _mesureur = document.createElement('canvas').getContext('2d');
    _mesureur.font = police;
    return _mesureur.measureText(texte).width;
}

/**
 * Place les mots et renvoie leurs positions, ou `null` si la zone n'est pas
 * encore mesurable. Le facteur d'échelle est réduit tant que tout ne rentre
 * pas : mieux vaut un nuage un peu plus petit que des mots jetés dehors —
 * « aucun filtre sur le nuage » est une décision du 19/09, elle vaut aussi
 * pour la place.
 */
function placer(mots, largeur, hauteur, familleDePolice) {
    if (!largeur || !hauteur || !mots.length) return null;

    const max = Math.max(...mots.map((m) => m.n));
    const min = Math.min(...mots.map((m) => m.n));

    // La taille de référence vient de la ZONE, pas de la fenêtre : c'est elle
    // qui doit être remplie. Bornée par la hauteur autant que par la largeur.
    const base = Math.min(largeur / 8, hauteur / 3.2);

    for (const facteur of [1, 0.88, 0.76, 0.64, 0.54, 0.45]) {
        const places = [];
        let tousPlaces = true;

        for (const m of mots) {
            // Racine plutôt que linéaire : sinon un mot donné dix fois écrase
            // tous les autres et le nuage n'a plus qu'un mot.
            const part = max === min ? 1 : (m.n - min) / (max - min);
            const taille = base * facteur * (0.42 + 0.58 * Math.sqrt(part));
            const police = `700 ${taille}px ${familleDePolice}`;
            const l = mesurer(m.mot, police) + taille * MARGE * 2;
            const h = taille * (1 + MARGE);

            let pose = false;
            // Pas angulaire serré pour les gros mots (ils ont besoin d'un
            // grand trou, on cherche finement), plus large ensuite.
            for (let t = 0; t < 620; t += 0.28) {
                const rayon = 2.2 * t;
                const x = rayon * Math.cos(t);
                const y = rayon * Math.sin(t) * RATIO_SPIRALE;

                if (Math.abs(x) + l / 2 > largeur / 2) continue;
                if (Math.abs(y) + h / 2 > hauteur / 2) continue;

                const heurte = places.some((p) =>
                    Math.abs(x - p.x) * 2 < l + p.l && Math.abs(y - p.y) * 2 < h + p.h);
                if (heurte) continue;

                places.push({ ...m, x, y, l, h, taille, part });
                pose = true;
                break;
            }
            if (!pose) { tousPlaces = false; break; }
        }

        if (tousPlaces) return places;
    }
    return null;
}

/**
 * LES TROIS RANGS DU NUAGE, et c'est ce qui lui donne son relief.
 *
 * Un nuage où tous les mots ont la même matière n'est qu'une liste en
 * désordre : seule la taille distingue, et de loin elle ne suffit pas. Les
 * mots les plus donnés reçoivent donc le dégradé du deck — celui des titres —
 * et un halo ; ceux du milieu restent clairs et pleins ; la traîne s'efface
 * dans le fond. On lit la hiérarchie avant d'avoir lu les mots.
 *
 * ⚠️ `bg-clip-text` COUPE LES JAMBES DESCENDANTES si la boîte colle au texte :
 * le dégradé ne peint que l'élément, et le « p » de « copilote » en sortait.
 * D'où le `pb-[0.12em]` sur les mots en dégradé — le même correctif que sur
 * BigNumberSlide.
 */
const RANGS = {
    // Le sommet : dégradé + halo, comme un titre du deck.
    vedette: 'bg-gradient-to-br from-cyan-200 via-white to-violet-200 bg-clip-text text-transparent pb-[0.12em] drop-shadow-[0_0_25px_rgba(34,211,238,0.45)]',
    fort: 'text-white/95 drop-shadow-[0_0_12px_rgba(148,197,255,0.25)]',
    moyen: 'text-cyan-100/90',
    traine: 'text-blue-200/55',
};

function Nuage({ mots }) {
    const zone = useRef(null);
    const [boite, setBoite] = useState({ largeur: 0, hauteur: 0 });
    const [police, setPolice] = useState('sans-serif');

    /* On mesure la ZONE, pas la fenêtre : la page de conférence met déjà le
       `rem` à l'échelle, et deux slides de même largeur n'ont pas la même
       hauteur utile. `ResizeObserver` couvre le redimensionnement, mais aussi
       le premier rendu, où la zone n'a pas encore de taille. */
    useLayoutEffect(() => {
        const el = zone.current;
        if (!el) return undefined;
        setPolice(window.getComputedStyle(el).fontFamily || 'sans-serif');
        const lire = () => {
            const r = el.getBoundingClientRect();
            setBoite({ largeur: Math.round(r.width), hauteur: Math.round(r.height) });
        };
        lire();
        const obs = new ResizeObserver(lire);
        obs.observe(el);
        return () => obs.disconnect();
    }, []);

    const places = useMemo(
        () => placer(mots, boite.largeur, boite.hauteur, police),
        [mots, boite.largeur, boite.hauteur, police],
    );

    return (
        <div ref={zone} className="relative w-full h-full">
            {places?.map((m, i) => {
                const vedette = i === 0 && m.part > 0;
                const rang = vedette ? 'vedette'
                    : m.part > 0.55 ? 'fort'
                        : m.part > 0.2 ? 'moyen' : 'traine';

                /* LA CASCADE SUIT LA SPIRALE : les mots sont déjà rangés du
                   plus gros au plus petit, donc du centre vers l'extérieur.
                   Le nuage se forme comme il a été construit. */
                const retard = Math.min(i * 45, 1400);

                return (
                    <span
                        key={m.mot}
                        className={`absolute whitespace-nowrap font-bold leading-none animate-mot ${RANGS[rang]} ${vedette ? 'animate-mot-vedette' : ''}`}
                        style={{
                            left: `calc(50% + ${Math.round(m.x)}px)`,
                            top: `calc(50% + ${Math.round(m.y)}px)`,
                            transform: 'translate(-50%, -50%)',
                            fontSize: `${m.taille}px`,
                            animationDelay: vedette ? `${retard}ms, ${retard + 700}ms` : `${retard}ms`,
                        }}
                        title={m.n > 1 ? `${m.n} fois` : undefined}
                    >
                        {m.mot}
                    </span>
                );
            })}
        </div>
    );
}

function Barres({ frequence, ton }) {
    const lignes = FREQUENCES.map((f) => ({ ...f, n: frequence?.[f.cle] || 0 }));
    const total = lignes.reduce((s, l) => s + l.n, 0);
    const max = Math.max(1, ...lignes.map((l) => l.n));

    return (
        <div className="flex flex-col gap-[2.2vh] w-full">
            {lignes.map((l, i) => {
                // « 3 fois par jour ou plus » est la ligne qui intéresse la
                // salle : c'est le rang qu'ils viennent de se donner, et celui
                // dont la conférence dit qu'il devient la norme.
                const vedette = l.cle === 'plus3';
                const part = total ? Math.round((100 * l.n) / total) : 0;
                return (
                    <div key={l.cle} className="animate-fade-in-up" style={{ animationDelay: `${i * 90}ms` }}>
                        <div className="flex items-baseline justify-between gap-3 mb-[0.4vh]">
                            <span
                                className={vedette ? `font-bold ${ton.texte}` : 'text-slate-300/80 font-light'}
                                style={{ fontSize: 'clamp(0.7rem, min(1.3vw, 2.4vh), 1.35rem)' }}
                            >
                                {l.libelle}
                            </span>
                            <span
                                className={`font-mono tabular-nums ${vedette ? ton.texte : 'text-slate-400/70'}`}
                                style={{ fontSize: 'clamp(0.7rem, min(1.2vw, 2.2vh), 1.25rem)' }}
                            >
                                {l.n}{total ? ` · ${part}%` : ''}
                            </span>
                        </div>
                        <div className="h-[2.2vh] min-h-[10px] rounded-full bg-white/[0.06] overflow-hidden">
                            <div
                                className={`h-full rounded-full transition-[width] duration-[1200ms] ease-out ${vedette
                                    ? 'bg-gradient-to-r from-blue-500 via-cyan-400 to-cyan-300 shadow-[0_0_20px_-4px_rgba(34,211,238,0.9)]'
                                    : 'bg-gradient-to-r from-blue-600/50 to-violet-500/40'
                                    }`}
                                style={{ width: `${(l.n / max) * 100}%` }}
                            />
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

const SalleSlide = ({ slide, slideKey }) => {
    const ton = accent(slide.accent);
    // Une seule lecture, à l'affichage : l'écran projeté ne bouge pas tout
    // seul pendant qu'Adrien parle. Voir `useSalleResultat`.
    const { resultat, erreur, chargement } = useSalleResultat();

    const mots = resultat?.mots || [];
    const synthese = resultat?.synthese || null;
    const questions = resultat?.questions ?? null;
    const contributions = resultat
        ? Object.values(resultat.frequence || {}).reduce((s, n) => s + n, 0)
        : 0;

    // Rien du tout : ni chiffres, ni mots, ni synthèse. On ne montre pas un
    // écran vide, on rend la parole à la salle — c'est le repli du conducteur.
    const vide = !chargement && !mots.length && !contributions && !synthese;

    /* LA VOIX IA LIT LA SYNTHÈSE — 5e et dernière apparition.
       Le MP3 est produit en même temps que le texte, par le bouton du
       prompteur : rien n'est généré ici, on ne fait que le jouer. F1, comme
       pour les quatre autres répliques (le prompteur relaie la touche, donc
       « F1 · voix » marche aussi à distance).
       ⚠️ Lecture locale et non via `useConferenceScenario` : ce hook joue les
       fichiers du dossier `public/`, or celui-ci vient du backend, protégé par
       le jeton admin. Conséquence assumée : l'orbe du 1er ministre ne s'anime
       pas, c'est l'encart qui s'éclaire. */
    const audioUrl = useSyntheseAudio({ actif: !!synthese?.audio });
    const lecteur = useRef(null);
    const [parle, setParle] = useState(false);

    useEffect(() => {
        if (!audioUrl) return undefined;
        const audio = new Audio(audioUrl);
        lecteur.current = audio;
        audio.onended = () => setParle(false);
        audio.onerror = () => setParle(false);

        const surTouche = (e) => {
            if (e.key !== 'F1') return;
            e.preventDefault();
            if (!audio.paused) {          // deuxième F1 : on coupe
                audio.pause();
                audio.currentTime = 0;
                setParle(false);
                return;
            }
            audio.play().then(() => setParle(true)).catch(() => setParle(false));
        };

        window.addEventListener('keydown', surTouche);
        return () => {
            window.removeEventListener('keydown', surTouche);
            audio.pause();
            lecteur.current = null;
        };
    }, [audioUrl]);

    return (
        <div key={slideKey} className="h-full flex flex-col px-16 pt-[2vh] pb-16 relative overflow-hidden">
            <div className="relative z-10 flex flex-col h-full gap-[2vh]">
                {/* ------------------------------------------------- titre -- */}
                <div className="flex items-end justify-between gap-6 flex-shrink-0">
                    <div>
                        {slide.kicker && (
                            <p
                                className={`font-mono uppercase tracking-[0.3em] opacity-70 ${ton.texte}`}
                                style={{ fontSize: 'clamp(0.65rem, min(1.2vw, 2.2vh), 1.4rem)' }}
                            >
                                {slide.kicker}
                            </p>
                        )}
                        <h1
                            className={`font-black leading-[1.1] bg-gradient-to-r ${ton.titre} bg-clip-text text-transparent`}
                            style={{ fontSize: 'clamp(1.6rem, min(4vw, 8vh), 3.75rem)' }}
                        >
                            {slide.title || 'La salle'}
                        </h1>
                    </div>

                    {/* Pas de « 0 question » au-dessus d'un écran qui dit déjà
                        qu'il n'a rien reçu : deux fois la même mauvaise
                        nouvelle, dont une en chiffre. */}
                    {questions != null && !vide && (
                        <p
                            className="text-slate-400/70 font-mono tabular-nums whitespace-nowrap"
                            style={{ fontSize: 'clamp(0.7rem, min(1.2vw, 2.2vh), 1.25rem)' }}
                        >
                            {questions} question{questions > 1 ? 's' : ''}
                            {erreur && <span className="ml-2 text-amber-400/70" title="Dernière lecture en échec">•</span>}
                        </p>
                    )}
                </div>

                {vide ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center gap-[2vh]">
                        <p
                            className="text-slate-200 font-light leading-snug max-w-4xl"
                            style={{ fontSize: 'clamp(1.1rem, min(2.4vw, 4.4vh), 2.4rem)' }}
                        >
                            Vos questions, maintenant — à voix haute.
                        </p>
                        <p
                            className="text-slate-500 font-mono"
                            style={{ fontSize: 'clamp(0.7rem, min(1.1vw, 2vh), 1.1rem)' }}
                        >
                            {erreur ? 'Réponses non lues' : 'Aucune réponse enregistrée'}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* ------------------------ fréquence · nuage de mots -- */}
                        {/* Les deux colonnes partagent la même structure — en-tête
                            en haut, contenu centré — sinon leurs deux intitulés
                            se retrouvent à des hauteurs différentes et l'écran
                            perd sa ligne de lecture. */}
                        <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[minmax(0,34%)_minmax(0,1fr)] gap-[3vw] items-stretch">
                            <div className="flex flex-col gap-[1.2vh] min-h-0">
                                <p
                                    className="text-slate-400/60 font-mono uppercase tracking-[0.2em] flex-shrink-0"
                                    style={{ fontSize: 'clamp(0.6rem, min(1vw, 1.8vh), 1rem)' }}
                                >
                                    Vous utilisez l'IA
                                </p>
                                <div className="flex-1 min-h-0 flex items-center">
                                    <Barres frequence={resultat?.frequence} ton={ton} />
                                </div>
                            </div>

                            <div className="flex flex-col gap-[1.2vh] min-h-0">
                                <p
                                    className="text-slate-400/60 font-mono uppercase tracking-[0.2em] text-center flex-shrink-0"
                                    style={{ fontSize: 'clamp(0.6rem, min(1vw, 1.8vh), 1rem)' }}
                                >
                                    L'IA, en un mot
                                </p>
                                <div className="flex-1 min-h-0 overflow-hidden">
                                    <Nuage mots={mots} />
                                </div>
                            </div>
                        </div>

                        {/* ------------------------------------- la synthèse -- */}
                        {/* ⚠️ L'ENCART RESTE BAS ET COMPACT. Il prenait
                            2,2 vh de rembourrage et jusqu'à 2 rem de texte :
                            sur un écran moins haut que le 16:9, il mordait sur
                            la zone du nuage, dont le conteneur coupe alors les
                            jambes des lettres. La synthèse se LIT de près et
                            s'ENTEND de loin — elle n'a pas besoin de la taille
                            d'un titre. */}
                        <div
                            className={`flex-shrink-0 rounded-2xl border bg-gradient-to-br ${ton.halo} px-[2vw] py-[1.4vh] backdrop-blur-sm transition-shadow duration-500 ${parle
                                ? 'border-cyan-300/80 shadow-[0_0_60px_-12px_rgba(34,211,238,0.85)]'
                                : ton.bord
                                }`}
                        >
                            {synthese ? (
                                <>
                                    <p
                                        className="text-white/95 font-light leading-snug"
                                        style={{ fontSize: 'clamp(0.85rem, min(1.55vw, 2.7vh), 1.5rem)' }}
                                    >
                                        {synthese.texte}
                                    </p>
                                    <p
                                        className="mt-[0.6vh] text-slate-400/60 font-mono tabular-nums"
                                        style={{ fontSize: 'clamp(0.55rem, min(0.85vw, 1.5vh), 0.9rem)' }}
                                    >
                                        Synthèse de vos {synthese.nb_questions} question
                                        {synthese.nb_questions > 1 ? 's' : ''} · {synthese.heure}
                                        {audioUrl && (
                                            <span className={`ml-2 ${parle ? 'text-cyan-300' : 'text-slate-500'}`}>
                                                {parle ? '▮▮▮ lecture' : '▸ F1'}
                                            </span>
                                        )}
                                        {questions > synthese.nb_questions && (
                                            <span className="ml-2 text-amber-300/70">
                                                +{questions - synthese.nb_questions} arrivée
                                                {questions - synthese.nb_questions > 1 ? 's' : ''} depuis
                                            </span>
                                        )}
                                    </p>
                                </>
                            ) : (
                                <p
                                    className="text-slate-300/70 font-light"
                                    style={{ fontSize: 'clamp(0.85rem, min(1.4vw, 2.5vh), 1.35rem)' }}
                                >
                                    {chargement ? 'Lecture de vos réponses…' : 'Vos questions sont là. On les prend une par une.'}
                                </p>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default SalleSlide;
